import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { fetchCustomers } from "../../../api/customer";
import { debounce } from "lodash";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

const perPage = 20;

/**
 * Supplier picker (customers with supplier / customer_company_supplier type).
 * UX aligned with ModernProjectSelector: search, list, single or multi select.
 */
export default function ModernSupplierSelector({
  open = false,
  selectedSuppliers = [],
  onChange,
  onClose,
  multiple = false,
  showHeader = true,
}) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedIds, setSelectedIds] = useState(
    new Set(selectedSuppliers.map((s) => s.id))
  );

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(selectedSuppliers.map((s) => s.id)));
    }
  }, [selectedSuppliers, open]);

  const loadPage = useCallback(async (query, page, append) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetchCustomers({
        query: query || undefined,
        page,
        per_page: perPage,
        supplier: true,
        sortBy: "name",
        sort: "asc",
      });
      if (!res || !Array.isArray(res.data)) {
        setError(t("tool.supplier.load_error"));
        if (!append) setRows([]);
        setTotalCount(0);
        setHasMore(false);
        return;
      }
      const data = res.data;
      const count = typeof res?.count === "number" ? res.count : data.length;
      if (append && page > 1) {
        setRows((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          const next = data.filter((r) => !seen.has(r.id));
          return [...prev, ...next];
        });
      } else {
        setRows(data);
      }
      setTotalCount(count);
      setHasMore(page * perPage < count);
    } catch (err) {
      console.error("Error loading suppliers:", err);
      setError(t("tool.supplier.load_error"));
      if (!append) setRows([]);
      setTotalCount(0);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const runSearch = useCallback(
    async (query, page, append) => {
      await loadPage(query, page, append);
    },
    [loadPage]
  );

  const debouncedSearchRef = useRef(debounce(runSearch, 300));

  useEffect(() => {
    debouncedSearchRef.current.cancel();
    debouncedSearchRef.current = debounce(runSearch, 300);
  }, [runSearch]);

  useEffect(() => {
    if (!open) {
      debouncedSearchRef.current.cancel();
      setSearchQuery("");
      setRows([]);
      setCurrentPage(1);
      setError(null);
      return;
    }
    setSearchQuery("");
    setCurrentPage(1);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const q = searchQuery.trim();
    if (q === "") {
      debouncedSearchRef.current.cancel();
      loadPage("", 1, false);
      return;
    }
    debouncedSearchRef.current(q, 1, false);
    return () => debouncedSearchRef.current.cancel();
  }, [searchQuery, open, loadPage]);

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      const next = currentPage + 1;
      setCurrentPage(next);
      runSearch(searchQuery.trim(), next, true);
    }
  }, [isLoading, hasMore, currentPage, searchQuery, runSearch]);

  const toggle = useCallback(
    (supplier) => {
      const next = new Set(selectedIds);
      if (next.has(supplier.id)) {
        next.delete(supplier.id);
      } else {
        if (!multiple) next.clear();
        next.add(supplier.id);
      }
      setSelectedIds(next);

      const selectedFromRows = rows.filter((r) => next.has(r.id));
      const selectedFromPrev = selectedSuppliers.filter(
        (s) => next.has(s.id) && !rows.some((r) => r.id === s.id)
      );
      const selected = [...selectedFromRows, ...selectedFromPrev];

      onChange?.(selected);

      if (!multiple && next.size > 0 && onClose) {
        setTimeout(() => onClose(), 200);
      }
    },
    [selectedIds, rows, selectedSuppliers, multiple, onChange, onClose]
  );

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    onChange?.([]);
  }, [onChange]);

  const isSelected = (id) => selectedIds.has(id);

  if (!open) return null;

  return (
    <div className="w-full h-full flex flex-col bg-white rounded-none sm:rounded-lg border border-gray-200 shadow-sm">
      {showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              {multiple ? t("tool.supplier.select_multiple") : t("tool.supplier.select_one")}
            </h3>
            <div className="flex items-center space-x-2">
              {selectedIds.size > 0 && (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-sm text-gray-500 hover:text-gray-700"
                  title={t("tool.supplier.clear")}
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
                  title={t("Cancel")}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("tool.supplier.search_placeholder")}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>

          {selectedIds.size > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              {t("tool.supplier.selected_count", { count: selectedIds.size })}
            </div>
          )}
        </div>
      )}

      {!showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("tool.supplier.search_placeholder")}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto max-h-[600px]">
        {isLoading && rows.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500">{t("Loading...")}</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-sm text-gray-500">{t("tool.supplier.empty")}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {rows.map((supplier) => {
              const sel = isSelected(supplier.id);
              const title = supplier.displayTitle || supplier.name || "—";
              return (
                <div
                  key={supplier.id}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggle(supplier);
                    }
                  }}
                  className={`w-full p-2 sm:p-3 border-b border-gray-100 last:border-b-0 ${
                    sel ? "bg-blue-50 border-l-4 border-l-blue-600" : ""
                  } ${!multiple ? "cursor-pointer hover:bg-gray-50" : ""}`}
                  onClick={!multiple ? () => toggle(supplier) : undefined}
                >
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle(supplier);
                        }}
                        className="focus:outline-none"
                      >
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            sel
                              ? "bg-blue-600 border-blue-600"
                              : "border-gray-300 hover:border-blue-400"
                          }`}
                        >
                          {sel && <CheckIcon className="h-4 w-4 text-white" />}
                        </div>
                      </button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
                      {supplier.customer_type && (
                        <p className="text-xs text-gray-500 mt-0.5">{supplier.customer_type}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {hasMore && !isLoading && rows.length > 0 && (
          <div className="p-4 text-center border-t border-gray-200">
            <button
              type="button"
              onClick={loadMore}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              {t("tool.supplier.load_more")}
            </button>
          </div>
        )}

        {isLoading && rows.length > 0 && (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" />
          </div>
        )}
      </div>
    </div>
  );
}
